import React, { useState } from 'react';
import {
  Search,
  Edit,
  Share2,
  Copy,
  MoreVertical,
  X,
  Check,
  Circle,
} from 'lucide-react';

interface Course {
  id: string;
  level: string;
  title: string;
  isOnline: boolean;
  nextClass: string;
  students: number;
  progress: number;
  attendance: number;
  totalHours: number;
}

interface TeacherCoursesTabProps {
  teacherId: string;
}

const mockCourses: Course[] = [
  {
    id: '1',
    level: 'YEAR 1 / COHORT A3',
    title: 'Introduction to Psychology',
    isOnline: true,
    nextClass: '8 Teachers',
    students: 24,
    progress: 75,
    attendance: 92,
    totalHours: 48,
  },
  {
    id: '2',
    level: 'YEAR 2 / COHORT B1',
    title: 'Web Development Bootcamp',
    isOnline: true,
    nextClass: '6 Teachers',
    students: 18,
    progress: 60,
    attendance: 88,
    totalHours: 72,
  },
  {
    id: '3',
    level: 'YEAR 1 / COHORT A1',
    title: 'Data Science Fundamentals',
    isOnline: false,
    nextClass: '4 Teachers',
    students: 30,
    progress: 45,
    attendance: 95,
    totalHours: 36,
  },
  {
    id: '4',
    level: 'YEAR 3 / COHORT C2',
    title: 'Digital Marketing Strategies',
    isOnline: true,
    nextClass: '5 Teachers',
    students: 22,
    progress: 80,
    attendance: 90,
    totalHours: 24,
  },
];

const allCourses = [
  { id: '1', name: 'Introduction to Psychology', assigned: true },
  { id: '2', name: 'Web Development Bootcamp', assigned: true },
  { id: '3', name: 'Data Science Fundamentals', assigned: true },
  { id: '4', name: 'Digital Marketing Strategies', assigned: true },
  { id: '5', name: 'and the science of weird science', assigned: false },
  { id: '6', name: 'Climate Change', assigned: false },
];

export function TeacherCoursesTab({ teacherId }: TeacherCoursesTabProps) {
  const [courses] = useState<Course[]>(mockCourses);
  const [searchQuery, setSearchQuery] = useState('');
  const [showManageModal, setShowManageModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [manageCourses, setManageCourses] = useState(allCourses);
  const [manageSearch, setManageSearch] = useState('');
  const [copied, setCopied] = useState(false);

  const filteredCourses = courses.filter((course) =>
    course.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEditCourse = (course: Course) => {
    setShowManageModal(true);
  };

  const handleShareCourse = (course: Course) => {
    setSelectedCourse(course);
    setShowShareModal(true);
  };

  const handleToggleCourseAssignment = (courseId: string) => {
    setManageCourses((prev) =>
      prev.map((c) => (c.id === courseId ? { ...c, assigned: !c.assigned } : c))
    );
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`https://viscool.edu/course/${selectedCourse?.id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const filteredManageCourses = manageCourses.filter((c) =>
    c.name.toLowerCase().includes(manageSearch.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Teacher Courses</h2>
            <p className="text-sm text-gray-500">Manage courses assigned to this teacher</p>
          </div>
          <button
            onClick={() => setShowManageModal(true)}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Manage Courses
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search courses..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        {/* Courses Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Students</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attendance</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredCourses.map((course) => (
                <tr key={course.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div>
                      <p className="text-xs text-gray-500">{course.level}</p>
                      <p className="font-medium text-gray-900">{course.title}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                      course.isOnline
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      <Circle className={`w-2 h-2 ${course.isOnline ? 'fill-green-500' : 'fill-gray-400'}`} />
                      {course.isOnline ? 'Online' : 'Offline'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{course.students}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-full bg-teal-500 rounded-full"
                          style={{ width: `${course.progress}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">{course.progress}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-sm font-medium ${
                      course.attendance >= 90
                        ? 'text-green-600'
                        : course.attendance >= 75
                        ? 'text-yellow-600'
                        : 'text-red-600'
                    }`}>
                      {course.attendance}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{course.totalHours}h</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEditCourse(course)}
                        className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleShareCourse(course)}
                        className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded"
                        title="Share"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manage Courses Modal */}
      {showManageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Teacher Courses</h3>
              <button
                onClick={() => setShowManageModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold">
                T
              </div>
              <div>
                <p className="font-medium text-gray-900">Teacher Name</p>
                <p className="text-sm text-gray-500">Change Teacher</p>
              </div>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={manageSearch}
                onChange={(e) => setManageSearch(e.target.value)}
                placeholder="Search"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* Course List */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {filteredManageCourses.map((course) => (
                <div
                  key={course.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50"
                >
                  <span className="text-sm text-gray-900">{course.name}</span>
                  <button
                    onClick={() => handleToggleCourseAssignment(course.id)}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      course.assigned
                        ? 'bg-teal-500 border-teal-500'
                        : 'border-gray-300 hover:border-teal-500'
                    }`}
                  >
                    {course.assigned && <Check className="w-3 h-3 text-white" />}
                  </button>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setManageCourses(allCourses);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Reset
              </button>
              <button
                onClick={() => setShowManageModal(false)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && selectedCourse && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Share Course</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Share "{selectedCourse.title}" with others
            </p>

            <div className="flex items-center gap-2 mb-4">
              <input
                type="text"
                value={`https://viscool.edu/course/${selectedCourse.id}`}
                readOnly
                className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-600"
              />
              <button
                onClick={handleCopyLink}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  copied
                    ? 'bg-green-100 text-green-700'
                    : 'bg-teal-600 text-white hover:bg-teal-700'
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>

            <div className="flex items-center justify-end">
              <button
                onClick={() => setShowShareModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
